import { Typography } from '@mui/material';
import { type Control, Controller } from 'react-hook-form';

import { RadioToggle } from '@/components/Form/RadioToggle';
import { ToggleGroup } from '@/components/Form/ToggleGroup';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { UserAccountType } from '@/types/user';

import { isNil } from '@/utils/common';
import { isCaymanUser } from '@/utils/user';

import type { AccountPurposeFormData } from '../drawers/QuestionsDrawer/QuestionAccountPurpose';

import {
  QuestionLabel,
  QuestionsContainerItem,
  QuestionsContainerItemYesNoContainer,
} from '../drawers/QuestionsDrawer/QuestionAboutMultipleMonthlyDeposit.styled';

type QuestionBusinessPurposeFormProps = {
  account: UserAccountType;
  index: number;
  control: Control<AccountPurposeFormData>;
};
export function QuestionBusinessPurposeForm({
  account,
  index,
  control,
}: Readonly<QuestionBusinessPurposeFormProps>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);

  const currency = account.currency;
  const cardTitle = `${account.name} (${currency})`;
  const isCayman = isCaymanUser(user);

  return (
    <QuestionsContainerItem key={account.id}>
      <Typography marginTop={2} marginBottom={2} variant="body1Medium">
        {cardTitle}
      </Typography>

      <QuestionLabel>
        {t('questionDrawer.accountPurpose.accountType.question')}
      </QuestionLabel>
      {isCayman ? (
        <QuestionsContainerItemYesNoContainer>
          <RadioToggle
            control={control}
            name={`accounts.${index}.isBusinessPurpose`}
            options={[
              { value: true, label: t('common.yes') },
              { value: false, label: t('common.no') },
            ]}
            rules={{
              required: t('questionDrawer.accountPurpose.accountType.required'),
            }}
          />
        </QuestionsContainerItemYesNoContainer>
      ) : (
        <Controller
          control={control}
          name={`accounts.${index}.isBusinessPurpose`}
          rules={{
            validate: value => {
              if (isNil(value)) {
                return t('questionDrawer.accountPurpose.accountType.required');
              }
              return true;
            },
          }}
          render={({ field }) => (
            <ToggleGroup
              fullWidth
              size="medium"
              options={[
                { value: 'false', text: t('common.no') },
                { value: 'true', text: t('common.yes') },
              ]}
              {...field}
              value={String(field.value)}
              onChange={(_event, value) => {
                field.onChange(value === 'true');
              }}
            />
          )}
        />
      )}
    </QuestionsContainerItem>
  );
}
